import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ArmazenamentoDeTokenService } from '../armazenamento/armazenamento-de-token.service';
import { Endpoints } from '../../utils/endpoints';

export interface Notificacao {
  id: number;
  tipo: string;
  titulo: string;
  mensagem: string;
  proposta_id: number | null;
  lida: boolean;
  lida_em: string | null;
  created_at: string;
}

interface NotificacoesResposta {
  data: Notificacao[];
  nao_lidas: number;
}

/**
 * O sino do topo do painel.
 *
 * Consulta em intervalo, e não em tempo real: sem service worker no painel, um
 * websocket seria infraestrutura nova para ganhar segundos num aviso que o
 * usuário lê quando olha para a tela.
 *
 * A consulta vai com `X-Show-Loading-Animation: false` — piscar o loader
 * global a cada minuto tornaria a tela inutilizável.
 */
@Injectable({ providedIn: 'root' })
export class NotificacaoApiService {
  private static readonly INTERVALO_MS = 60_000;

  private readonly api = environment.APIEntryPoint_1;
  private readonly httpClient = inject(HttpClient);
  private readonly token = inject(ArmazenamentoDeTokenService);

  readonly notificacoes = signal<Notificacao[]>([]);
  readonly naoLidas = signal(0);

  private cronometro?: ReturnType<typeof setInterval>;

  /** Idempotente: chamar duas vezes não duplica o intervalo. */
  iniciarPolling(): void {
    this.pararPolling();
    this.carregar();
    this.cronometro = setInterval(
      () => this.carregar(),
      NotificacaoApiService.INTERVALO_MS
    );
  }

  pararPolling(): void {
    if (this.cronometro) {
      clearInterval(this.cronometro);
      this.cronometro = undefined;
    }
  }

  carregar(): void {
    // Sem sessão a consulta só renderia 401 em laço, e o interceptor de erro
    // derrubaria o login de quem está justamente tentando entrar.
    if (!this.token.buscarToken()) {
      return;
    }

    this.buscar().subscribe({
      next: (resposta) => {
        this.notificacoes.set(resposta.data ?? []);
        this.naoLidas.set(resposta.nao_lidas ?? 0);
      },
      // Falha de rede não pode encher a tela de erro: o sino simplesmente não
      // atualiza desta vez.
      error: () => undefined,
    });
  }

  marcarLida(id: number): Observable<unknown> {
    return this.httpClient.post(
      `${this.api}/${Endpoints.notificacaoLida.replace('{id}', String(id))}`,
      {},
      this.opcoes()
    );
  }

  marcarTodasLidas(): Observable<unknown> {
    return this.httpClient.post(
      `${this.api}/${Endpoints.notificacoesLidas}`,
      {},
      this.opcoes()
    );
  }

  /** Reflete a leitura na lista já carregada, sem esperar o próximo intervalo. */
  aplicarLeitura(id: number): void {
    this.notificacoes.update((lista) =>
      lista.map((n) => (n.id === id ? { ...n, lida: true } : n))
    );
    this.naoLidas.update((total) => Math.max(0, total - 1));
  }

  aplicarLeituraDeTodas(): void {
    this.notificacoes.update((lista) => lista.map((n) => ({ ...n, lida: true })));
    this.naoLidas.set(0);
  }

  limpar(): void {
    this.pararPolling();
    this.notificacoes.set([]);
    this.naoLidas.set(0);
  }

  private buscar(): Observable<NotificacoesResposta> {
    return this.httpClient.get<NotificacoesResposta>(
      `${this.api}/${Endpoints.notificacoes}`,
      this.opcoes()
    );
  }

  private opcoes() {
    return {
      headers: new HttpHeaders().set('X-Show-Loading-Animation', 'false'),
    };
  }
}
